import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { startCheckout } from "@/utils/checkout";
import { toast } from "sonner";
import "@/landing-v5.css";

const PAIN_ROWS = [
  { bad: true, h: "Hire a ghostwriter for $18K–$35K", p: "You hope they capture your voice. No guarantee the topic will sell." },
  { bad: false, h: "Idea Evaluator validates in 60 seconds", p: "Live data tells you if people are searching and buying. Zero guesswork." },
  { bad: true, h: "Write it yourself for 3-6 months", p: "Most authors never finish. Hundreds of hours on a single manuscript." },
  { bad: false, h: "Book Studio writes in a weekend", p: "Researches, structures chapters, drafts full manuscript. You review and publish." },
  { bad: true, h: "Pay $250+ for a cover designer", p: "Wait a week. Iterate. Pay again if you hate it. No data on what converts." },
  { bad: false, h: "Cover Studio generates 4 in 90 seconds", p: "Precision prompts engineered for your title, audience, and genre. Ready for Midjourney." },
  { bad: true, h: "Hire a marketer for sales copy + SEO", p: "Another $500–$2,000 for a description, keywords, and categories." },
  { bad: false, h: "Launch pack included in the suite", p: "Sales page, back cover blurb, 7 keywords, 5 sub-categories, 156 video scripts. All in one place." },
];

const HOOK_FLOW = [
  { i: "🔍", h: "1. Find & Validate", p: "Idea Evaluator scans live data to find problems people search for — so you never write a book nobody wants." },
  { i: "📝", h: "2. Write The Book", p: "Book Studio researches, structures, and drafts every chapter. Value-laden content that solves real problems." },
  { i: "🎨", h: "3. Design The Cover", p: "Cover Studio reads your manuscript and writes 4 precision prompts. Scroll-stopping covers in 90 seconds." },
  { i: "📢", h: "4. Launch & Promote", p: "Sales page, keywords, categories, and 52 weeks of short-form video scripts — ready to post or hand to a VA." },
];

const TOOLS = [
  {
    ribbon: "Tool 1 · Viral Idea Evaluator",
    h: "Never Write A Book The Market Doesn't Want",
    p: "The biggest reason books fail: nobody checked if people are actually searching. The Idea Evaluator runs your concept through a $100K investment test — scoring viral potential, pain point depth, emotional triggers, dream outcome clarity, and market positioning. If your idea is weak, it tells you exactly what's missing and how to fix it.",
    li: [
      "Validates demand using live internet data — not guesses",
      "Scores 5 dimensions: viral potential, pain depth, emotion, positioning, dream outcome",
      "Suggests specific angles and hooks to strengthen weak ideas",
      "Saved library to revisit and compare past evaluations",
    ],
    img: "/sales/idea-evaluator-v5.png", alt: "Viral Idea Evaluator", reverse: false,
  },
  {
    ribbon: "Tool 2 · Viral Book Studio",
    h: "From Concept To Complete Book In One Flow",
    p: "Not a ChatGPT prompt box. Book Studio researches your niche, identifies your audience and their specific problems, analyses competing books, then writes the full manuscript chapter by chapter. It also generates your title suite, book description, 7 SEO keywords, 5 Amazon sub-categories, and sales funnel copy. One pipeline from concept to publish-ready file.",
    li: [
      "Coach research — finds audience, problems, and competitors from live data",
      "5 scored concepts + full title suite + Amazon sales copy",
      "Every chapter written with structure, clarity, and genuine value",
      "Launch pack: sales page, creator brief, email funnel, QR strategy",
    ],
    img: "/sales/book-studio-v5.png", alt: "Viral Book Studio", reverse: true,
  },
  {
    ribbon: "Tool 3 · Viral Book Cover Studio",
    h: "4 Viral Cover Prompts In 90 Seconds",
    p: "Upload your manuscript — the studio reads it, extracts your book's core promise and audience, and writes 4 precision cover prompts tailored to your exact book. Each prompt is engineered for your genre, your title, your target reader. Copy-paste into Midjourney, DALL-E, or Ideogram for professional covers in minutes.",
    li: [
      "Auto-fills book details from your uploaded manuscript",
      "Prompts in English, Spanish, and French for global reach",
      "Genre-specific styling — 14+ genres supported",
      "4 distinct concepts per book — pick your strongest and iterate",
    ],
    img: "/sales/cover-studio-v5.png", alt: "Viral Book Cover Studio", reverse: false,
  },
  {
    ribbon: "Tool 4 · PubGlot Translator",
    h: "One Book. 15+ Languages. Zero Rewriting.",
    p: "Upload your finished manuscript as .docx, .pdf or .epub. PubGlot extracts the text, translates it while preserving formatting, and returns a polished file ready for distribution. Spanish, French, German, Portuguese and more — each edition becomes a separate listing on IngramSpark with its own royalty stream.",
    li: [
      "Preserves formatting — no manual rebuilding after translation",
      "Supports .docx, .pdf, and .epub files",
      "Spanish editions consistently outperform English originals in my catalogue",
      "One-time work per language. Ongoing royalties forever.",
    ],
    img: "/sales/pubglot-v5.png", alt: "PubGlot Translator", reverse: true,
  },
];

const TESTIMONIALS = [
  { a: "S", n: "Samuel T.", r: "Indie Publisher · Year 1", q: "\"Two of my books are on IngramSpark in 3 categories each. The Idea Evaluator picked the niches. I just followed the system. Up to $1,800/month.\"", res: "$1,800/month from 12 books" },
  { a: "O", n: "Olivia M.", r: "Newsletter Writer · Personal Finance", q: "\"Used Book Studio to expand my best post into a 28k-word book on a Saturday. Listed it Monday. First $2,000 month was 6 weeks later.\"", res: "$2,000/month in 6 weeks" },
  { a: "A", n: "Aisha K.", r: "Author · Christian Devotionals", q: "\"PubGlot turned my one English book into 4 editions. Spanish alone now earns more than the original. I literally did the work once.\"", res: "Translation doubled income" },
  { a: "J", n: "Jordan P.", r: "Career Coach", q: "\"Used VIP to spin up 3 niche workbooks in two weekends. Sold 40 copies of each before I'd even told my list.\"", res: "40 copies before promotion" },
  { a: "M", n: "Marcus B.", r: "Self-Published Author · Productivity", q: "\"Now I generate 4 ready-to-render cover prompts in 90 seconds. Click-through on my book pages doubled in the first month.\"", res: "2x click-through rate" },
  { a: "L", n: "Lauren H.", r: "VA Agency Owner", q: "\"We onboarded VIP, ran 6 client books through it the first month, and increased our retainer pricing by 40%.\"", res: "40% higher retainer pricing" },
];

const FAQ = [
  { q: "Do I need to have written a book before?", a: "No. Most VIP members are first-time authors. The Book Studio writes the full manuscript from a validated concept. Your 1:1 call ensures you start right." },
  { q: "Is this a monthly fee?", a: "No. Every tier is a one-time payment. No subscriptions. No recurring fees. Pay once, keep the tools forever." },
  { q: "I have zero experience. Can I do this?", a: "Yes. The system was built for beginners. Tools guide you through every step. VIP includes a 1:1 call that picks your niche and plans your first 90 days." },
  { q: "How much time does this take?", a: "First book: ~7 days from idea to upload. One per week after that. With VA training (VIP), you can delegate the entire process." },
  { q: "Where do I sell my books?", a: "IngramSpark distributes to 40,000+ retailers — Amazon, Barnes & Noble, Waterstones, libraries worldwide. Amazon KDP is optional." },
  { q: "Is this just a ChatGPT wrapper?", a: "No. Each tool is purpose-built — research, validation, manuscript structure, cover prompts, translation. A complete publishing system." },
  { q: "What if it doesn't work for me?", a: "Full refund within 14 days. No hoops. You keep everything you downloaded. Zero risk." },
  { q: "Can I use this outside the US?", a: "Yes. IngramSpark works globally. Authors from 100+ countries use this system." },
];

function useMidnightCountdown() {
  const calc = () => {
    const n = new Date();
    const e = new Date(n);
    e.setHours(23, 59, 59, 0);
    const d = Math.max(0, e - n);
    const pad = (x) => String(x).padStart(2, "0");
    return {
      h: pad(Math.floor(d / 3600000)),
      m: pad(Math.floor((d % 3600000) / 60000)),
      s: pad(Math.floor((d % 60000) / 1000)),
    };
  };
  const [t, setT] = useState(calc);
  useEffect(() => {
    const id = setInterval(() => setT(calc()), 1000);
    return () => clearInterval(id);
  }, []);
  return t;
}

function CtaBlock({ testid }) {
  return (
    <div className="cta-block">
      <span className="old">Starting from $497</span>
      <span className="price-tag">
        <span className="main-price"><span className="dollar">$</span>97</span>
        <span className="divider"></span>
        <span style={{ fontSize: 16, color: "#ccc", fontWeight: 400, verticalAlign: "middle" }}>TODAY</span>
      </span>
      <span className="price-label">One-time payment · Lifetime access · No recurring fees</span>
      <br />
      <a href="#pricing" className="btn-hero" data-testid={testid}>YES! BUILD MY ROYALTY INCOME NOW</a>
      <div className="cta-guarantee">
        <span className="shield">🛡</span>14-Day Money-Back Guarantee <span className="bolt">⚡</span> Instant Access
      </div>
    </div>
  );
}

export default function Landing() {
  const { hash } = useLocation();
  const nav = useNavigate();
  const { user } = useAuth();
  const cd = useMidnightCountdown();

  useEffect(() => {
    if (!hash) return;
    const id = hash.replace("#", "");
    const el = document.getElementById(id);
    if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  }, [hash]);

  const buyCta = async (tierId) => {
    if (!user) {
      sessionStorage.setItem("vbs_pending_tier", tierId);
      return nav("/stripe-signup?next=checkout");
    }
    try { await startCheckout(tierId); }
    catch { toast.error("Could not start checkout"); }
  };

  return (
    <div className="vbs5">
      {/* URGENCY BAR */}
      <div className="urgency" data-testid="countdown-bar">
        <div className="w">
          <p>🔥 EARLY BIRD PRICING EXPIRES IN:{" "}
            <span className="tmr">
              <span data-testid="cd-h">{cd.h}</span>:<span data-testid="cd-m">{cd.m}</span>:<span data-testid="cd-s">{cd.s}</span>
            </span>
            {" "}— PRICE RISES TO $1,497 WHEN TIMER HITS ZERO
          </p>
        </div>
      </div>

      {/* HERO */}
      <div className="hero">
        <div className="wn">
          <div className="logo-bar">
            <Link to="/" data-testid="landing-logo">
              <img src="/sales/logo.png" alt="Viral Book Suite" />
            </Link>
          </div>

          <div className="breaking-row">
            <div className="stars-row">
              <span className="star">★</span><span className="star">★</span><span className="star">★</span><span className="star">★</span><span className="star">★</span>
              <span className="served">2,147+ authors served</span>
            </div>
            <div className="breaking-banner">
              <span className="breaking-dot"></span>
              <span className="breaking-label">BREAKING:</span>
              <span className="breaking-text">The Suite of AI Tools Ordinary People Are Using To Earn $1200+ Monthly Income From Books They Create Once</span>
            </div>
          </div>

          <h1>New AI Suite of Tools Helps You Create Problem Solving Non-Fiction Books That Generate Monthly Royalty Income <span className="hl">Even When You Sleep</span></h1>

          <p className="hero-sub">Get your hands on the same suite of tools that has generated over $120k in monthly royalty income since 2023. The suite researches problems people are already searching for, validates your idea, writes the book, creates 4 scroll-stopping cover prompts, generates your sales page and metadata, then produces 52 weeks of viral content to promote it.</p>

          <CtaBlock testid="hero-cta-pricing" />

          <div className="hero-metrics">
            <div className="hm"><div className="hm-n">200+</div><div className="hm-l">Books Published</div></div>
            <div className="hm"><div className="hm-n">$120K+</div><div className="hm-l">Royalties Earned</div></div>
            <div className="hm"><div className="hm-n">1,400+</div><div className="hm-l">Authors Coached</div></div>
            <div className="hm"><div className="hm-n">40K+</div><div className="hm-l">Retailers Reached</div></div>
          </div>
        </div>
      </div>

      {/* OLD WAY VS SUITE WAY */}
      <section>
        <div className="wn">
          <h2 className="st tc">The Old Way vs <span className="hl">The Suite Way</span></h2>
          <div className="pain-row" style={{ marginTop: 24 }}>
            {PAIN_ROWS.map((r) => (
              <div key={r.h} className="pain-item">
                <div className="icon" style={{ color: r.bad ? "#e05050" : "var(--gold)" }}>{r.bad ? "✗" : "✓"}</div>
                <div><h4>{r.h}</h4><p>{r.p}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PIPELINE */}
      <section className="hook">
        <div className="wn">
          <h2>The Complete Pipeline. <span className="hl">One Suite.</span></h2>
          <div className="hook-flow">
            {HOOK_FLOW.map((f) => (
              <div key={f.h} className="hf-item">
                <div className="i">{f.i}</div>
                <h4>{f.h}</h4>
                <p>{f.p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TOOLS */}
      <section className="tools-section">
        <div className="ww">
          <h2 className="st tc">Every Tool, <span className="hl">With A Purpose</span></h2>
          <p className="ss tc mx" style={{ marginBottom: 28 }}>Four proprietary tools. One unified workflow. The same system behind a $1,200+/month royalty catalogue.</p>

          {TOOLS.map((t) => (
            <div key={t.ribbon} className={"tool-block" + (t.reverse ? " reverse" : "")}>
              <div className="visual">
                <img src={t.img} alt={t.alt} loading="lazy" />
              </div>
              <div className="desc">
                <div className="ribbon">{t.ribbon}</div>
                <h3>{t.h}</h3>
                <p>{t.p}</p>
                <ul>
                  {t.li.map((l) => <li key={l}>{l}</li>)}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CREATOR */}
      <section className="creator-section">
        <div className="wn">
          <h2 className="st tc">Meet The Creator Of <span className="hl">Viral Book Suite</span></h2>
          <div className="creator-row" style={{ marginTop: 28 }}>
            <div className="photo">
              <img src="/sales/mayowa-v5.jpg" alt="Mayowa Ajisafe" loading="lazy" data-testid="creator-photo" />
            </div>
            <div className="bio">
              <div className="name">Mayowa Ajisafe</div>
              <p className="sub">Publisher · 200+ Books · Creator of the Evergreen Royalties System</p>
              <p>In 2022, my 10-year-old Amazon KDP account was banned in a single evening. <span className="hl-g">$479 in unpaid royalties, gone.</span> No warning. No appeal. I had formatted over 1,400 books for other people. I knew publishing inside out. None of it mattered.</p>
              <p>That night I made a decision: I would never build a business that depends on any single platform's goodwill again. I rebuilt on IngramSpark, one upload reaches <span className="hl-g">40,000+ retailers</span> worldwide. Then I built a production system using AI to produce problem-solving books at scale.</p>
              <p>Today my catalogue generates <span className="hl-g">$2000+ every month</span> from 200+ titles. I spend 30 minutes reviewing data. The system does the rest.</p>
              <p>Viral Book Suite is the software I built to run my own operation. Every tool in this suite solves a specific problem I faced building my catalogue. I'm not selling theory, I'm selling the <span className="hl-g">exact engine</span> that powers a real, working royalty income system.</p>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section>
        <div className="w">
          <h2 className="st tc">Real People. <span className="hl">Real Results.</span></h2>
          <p className="ss tc mx" style={{ marginBottom: 28 }}>Authors using the suite to build real royalty income.</p>
          <div className="testi-row">
            {TESTIMONIALS.map((t) => (
              <div key={t.n} className="testi-card">
                <div className="init">{t.a}</div>
                <div className="nm">{t.n}</div>
                <div className="rl">{t.r}</div>
                <div className="qt">{t.q}</div>
                <div className="res">{t.res}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="pricing-section" id="pricing">
        <div className="w">
          <h2 className="st tc">No Monthly Fees. <span className="hl">No Subscriptions.</span></h2>
          <p className="ss tc mx" style={{ margin: "0 auto 0" }}>Pick the tier that matches how you want to build your catalogue.</p>

          <div className="pricing-grid" data-testid="pricing-grid">
            <div className="pcard" data-testid="tier-studio">
              <div className="tier">Viral Book Studio</div>
              <div className="price">$97</div>
              <div className="price-sub">one-time payment</div>
              <p className="desc">The book creation engine — write and publish faster on your own.</p>
              <ul>
                <li>Full AI manuscript engine</li>
                <li>5 scored concepts + title suite</li>
                <li>Sales copy, keywords, sub-categories</li>
                <li className="na">No Cover Studio — cover prompts not included</li>
                <li className="na">No Idea Evaluator — idea validation not included</li>
                <li className="na">No PubGlot translation</li>
              </ul>
              <button type="button" className="btn-pcard-outline" onClick={() => buyCta("studio")} data-testid="cta-tier-studio">Get Book Studio</button>
            </div>

            <div className="pcard featured" data-testid="tier-suite">
              <div className="tier">The Viral Book Suite</div>
              <div className="price">$497</div>
              <div className="price-sub">one-time payment</div>
              <p className="desc">All four core tools — find the right idea, write the book, design the cover.</p>
              <ul>
                <li>Viral Book Studio — full manuscript + launch pack</li>
                <li>Viral Book Cover Studio — 4 AI cover prompts per book</li>
                <li>Viral Idea Evaluator — live data demand scoring</li>
                <li>Quick-start workflow guide</li>
                <li className="na">No PubGlot translation</li>
                <li className="na">No 1:1 coaching call</li>
              </ul>
              <button type="button" className="btn-pcard" onClick={() => buyCta("suite")} data-testid="cta-tier-suite">Get The Suite</button>
            </div>

            <div className="pcard" data-testid="tier-vip">
              <div className="tier">Viral Book Suite VIP</div>
              <div className="price">$997</div>
              <div className="price-sub">one-time payment</div>
              <p className="desc">Everything — tools, translation, personal coaching, and delegation systems.</p>
              <ul>
                <li>Everything in Viral Book Suite</li>
                <li>PubGlot — 15+ language translator</li>
                <li>45-min 1:1 onboarding call with Mayowa</li>
                <li>Complete system video walkthrough</li>
                <li>VA training + delegation playbook</li>
                <li>Publishing playbook + formatting kit</li>
                <li>30 days direct email support</li>
              </ul>
              <button type="button" className="btn-pcard" onClick={() => buyCta("vip")} data-testid="cta-tier-vip">Get VIP Access</button>
            </div>
          </div>

          <p className="pricing-footnote">🔒 Secure Payment via Stripe · Instant Access · Questions? Email <a href="mailto:hello@viralbooksuite.com">hello@viralbooksuite.com</a></p>

          <div className="guarantee-box">
            <div className="shield">🛡</div>
            <h3>The "Do The Work" Money-Back Promise</h3>
            <p>Go through the tools. Attend your onboarding call. Do the work. If you genuinely try and don't feel this was worth it — email hello@viralbooksuite.com within 14 days. Full refund. You keep everything. No hoops. No questions.</p>
          </div>
        </div>
      </section>

      {/* NOT FOR EVERYONE */}
      <section>
        <div className="wn">
          <h2 className="st tc">⚠️ This Is <span className="hl">Not For Everyone</span></h2>
          <div className="warning-grid" style={{ marginTop: 20 }}>
            <div className="warning-col dont">
              <h4>Don't Buy If You:</h4>
              <ul>
                <li><span className="mk">✗</span> Want to get rich overnight with zero effort</li>
                <li><span className="mk">✗</span> Think one book will replace your income in 30 days</li>
                <li><span className="mk">✗</span> Refuse to invest time learning the system</li>
                <li><span className="mk">✗</span> Believe AI means you don't need to direct or review quality</li>
              </ul>
            </div>
            <div className="warning-col do">
              <h4>Perfect For You If:</h4>
              <ul>
                <li><span className="mk">✓</span> Want to build a real passive income asset over 6-12 months</li>
                <li><span className="mk">✓</span> Are ready to follow a proven system consistently</li>
                <li><span className="mk">✓</span> Want to publish books that solve real problems and actually sell</li>
                <li><span className="mk">✓</span> Understand that $200 reinvested in month 2 becomes $2,000 in month 12</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section>
        <div className="w">
          <h2 className="st tc">Answers To <span className="hl">Your Questions</span></h2>
          <div className="faq-grid" style={{ marginTop: 24 }}>
            {FAQ.map((f) => (
              <div key={f.q} className="faq-card">
                <h4>{f.q}</h4>
                <p>{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <div className="final-cta">
        <div className="wn">
          <h2 className="st">The Best Time To Start<br />Was 6 Months Ago. <span className="hl">The Second Best Time Is Now.</span></h2>
          <p>Your catalogue won't build itself. But with the right system, it almost feels that way.</p>
          <div style={{ marginTop: 16 }}>
            <CtaBlock testid="final-cta-pricing" />
          </div>
        </div>
      </div>

      {/* FOOTER */}
      <footer data-testid="marketing-footer">
        <div className="wn">
          <div className="footer-links">
            <Link to="/stripe-signin" data-testid="footer-login-link">Login</Link>
            <a href="https://www.theevergreenroyalties.com/" target="_blank" rel="noreferrer" data-testid="footer-newsletter-link">Join The Evergreen Royalties System Newsletter</a>
            <Link to="/privacy" data-testid="footer-privacy-link">Privacy Policy</Link>
            <Link to="/terms" data-testid="footer-terms-link">Terms</Link>
          </div>
          <div className="footer-divider"></div>
          <p>This site is not part of Amazon.com or endorsed by Amazon in any way.</p>
          <p>Earnings Disclaimer: Results stated above are personal results and those of top students. Results are not typical. Your results will vary and depend on many factors including your niche, consistency, and work ethic.</p>
          <p>The average person who buys any "how to" product gets little to no results when they don't put in the work, time and effort required to get result. This is in no way a get rich quick scheme.</p>
          <p className="copy">© {new Date().getFullYear()} ViralBookSuite.com. All Rights Reserved.</p>
        </div>
      </footer>
    </div>
  );
}
